import React from "react";
import { Skeleton, SkeletonGroup } from "../../../../components/common/Skeleton/Skeleton.jsx";
import { formatDateLabel } from "../../classroomSchedule.js";

/* The day grid -- the "Lab Availability" sheet the branches kept by hand:
   half-hour slots down, and across them labs (Overview, Labs timeline) or
   trainers (Trainers timeline). A lab cell reads "batch trainer (students)",
   a trainer cell "batch lab (students)". Every column, cell and label
   arrives resolved from GET /classroom-management/schedule/slots; this only
   draws it.

   A cancelled session stays in its cell, struck through. A column heading
   or a filled cell opens that lab's or trainer's day popup; a free cell
   books into that slot when the reader may schedule. Every row shows in
   full; the time column holds still when many columns scroll sideways. */
const entryTitle = (e) => {
  const parts = [e.label];
  if (e.cancelled) parts.push(`Not running: ${e.cancelReason || "cancelled"}`);
  if (e.shifted) parts.push("Moved into this lab for the day");
  return parts.join(" — ");
};

const SlotGrid = ({ grid, loading, failed, showDate, onColumnOpen, onFreeSlot }) => {
  if (loading) {
    return (
      <SkeletonGroup label="Loading the day" className="cls-tl-scroll lms-skel-stack">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton shape="row" key={i} />
        ))}
      </SkeletonGroup>
    );
  }
  if (failed || !grid) {
    return (
      <div className="pg-state">
        <p className="pg-state-text">The day could not be loaded. Try again in a moment.</p>
      </div>
    );
  }

  const isTrainers = grid.kind === "trainers";
  const columns = grid.columns || [];
  const slots = grid.slots || [];
  const noun = isTrainers ? "trainer" : "lab";

  if (!columns.length) {
    return (
      <div className="pg-state">
        <h3 className="pg-state-title">No {noun}s match</h3>
        <p className="pg-state-text">
          {isTrainers
            ? "No trainer fits the search above. Clear it to see them all."
            : "No lab fits the filters above. Clear the floor, status or search."}
        </p>
      </div>
    );
  }

  return (
    <section className="cls-slotgrid-wrap">
      <p className="cls-month-note">
        {showDate && `${formatDateLabel(grid.date, { weekday: "long", month: "short", day: "numeric", year: "numeric" })} · `}
        {columns.length} {columns.length === 1 ? noun : `${noun}s`} · click a {noun} or a booked slot to see its day
      </p>
      <div className="cls-tl-scroll cls-slotgrid-scroll">
        <table className="cls-slotgrid">
          <thead>
            <tr>
              <th scope="col" className="cls-slotgrid-time">Time</th>
              {columns.map((col) => (
                <th key={col.key} scope="col" className={col.out ? "is-out" : undefined}>
                  <button
                    type="button"
                    className="cls-slotgrid-lab"
                    onClick={() => onColumnOpen(col.row)}
                    title={col.out ? col.outTitle : `Open ${col.label}`}
                  >
                    {col.label}
                    {col.meta ? ` ${col.meta}` : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => (
              <tr key={slot.startMin} className={slot.startMin % 60 === 0 ? "is-hour" : undefined}>
                <th scope="row" className="cls-slotgrid-time">{slot.label}</th>
                {columns.map((col) => {
                  const cell = slot.cells[col.key];
                  if (cell) {
                    const tone = cell.clash || cell.overCapacity ? " is-warn" : "";
                    const warn = [
                      cell.clash && (isTrainers ? "Two batches at once" : "Two batches in one lab"),
                      cell.overCapacity && "More students than seats",
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <td key={col.key} className={`is-busy${tone}`}>
                        <button
                          type="button"
                          className="cls-slotgrid-cell"
                          onClick={() => onColumnOpen(col.row)}
                          title={[...cell.entries.map(entryTitle), warn].filter(Boolean).join("\n")}
                        >
                          {cell.entries.map((e) => (
                            <span
                              key={e.batchId}
                              className={`cls-slotgrid-entry${e.cancelled ? " is-cancelled" : ""}${e.shifted ? " is-shifted" : ""}`}
                            >
                              {e.label}
                            </span>
                          ))}
                        </button>
                      </td>
                    );
                  }
                  if (col.out || !onFreeSlot) {
                    return <td key={col.key} className={col.out ? "is-out" : undefined} />;
                  }
                  return (
                    <td key={col.key}>
                      <button
                        type="button"
                        className="cls-slotgrid-cell cls-slotgrid-free"
                        onClick={() => onFreeSlot(col.row, slot)}
                        aria-label={`Book ${col.label} at ${slot.label}`}
                        title={`Free — book ${col.label} at ${slot.label}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SlotGrid;
